import { useEffect, useState, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { CalendarClock, CircleCheck, MapPin, Search, Star } from "lucide-react";
import { buttonClass } from "@/design/buttons";
import { CategoryIcon } from "@/design/CategoryIcon";
import { Reveal } from "@/design/Reveal";
import { get } from "~/lib/api";
import { PublicPage } from "~/components/SiteChrome";

/**
 * LandingScreen (docs/ux/screens/LandingScreen.md), the public front door.
 *
 * Honest copy only: no payments, reminders, WhatsApp, paid plans or fabricated
 * stats. Bookr connects Consumers to approved businesses and the business
 * confirms each request. The appointment card in the hero is a labelled
 * illustration of that flow, not a real booking.
 *
 * **The category chips are the one thing here that needs the API**, and the
 * page is deliberately built so that not having them yet is a non-event: the
 * hero, the search box and everything below render immediately and the chips
 * appear when they arrive. A front door that shows a skeleton while it waits
 * for a decorative row would be slower for no gain, and one that failed
 * because a decorative row failed would be worse still.
 */
interface Category {
  id: string;
  name: string;
}

const STEPS = [
  {
    icon: Search,
    title: "Search & filter",
    body: "Find businesses near you by category, distance, price, and rating.",
  },
  {
    icon: CalendarClock,
    title: "Request a time",
    body: "Pick a service and an open time block, then send your request.",
  },
  {
    icon: CircleCheck,
    title: "The business confirms",
    body: "When they approve, you get a notification in your inbox. No calls.",
  },
];

export function LandingScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    void get<{ categories: Category[] }>("/categories", { anonymous: true }).then(
      (result) => {
        if (!cancelled && result.ok) setCategories(result.value.categories);
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  // H34: where a deletion lands. The counts arrive in the URL because the
  // account they describe no longer exists, so there is nowhere else to read
  // them from, and they identify nobody.
  const deletion =
    params.get("deleted") === "1"
      ? {
          cancelled: Number(params.get("cancelled") ?? 0) || 0,
          kept: Number(params.get("kept") ?? 0) || 0,
        }
      : null;

  return (
    <PublicPage>
      {deletion && (
        // **It says what a clinic kept, not just that the account is gone.**
        // That was the promise the confirmation made, and this is the last
        // moment it can be kept: there is no account left to tell afterwards.
        <div className="mx-auto w-full max-w-3xl px-4 pt-6">
          <div className="rounded-xl border border-line bg-surface p-5 text-sm leading-relaxed">
            <p className="font-semibold text-ink">
              Your account has been deleted.
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-body">
              {deletion.cancelled > 0 && (
                <li>
                  {deletion.cancelled === 1
                    ? "1 upcoming appointment was cancelled."
                    : `${deletion.cancelled} upcoming appointments were cancelled.`}
                </li>
              )}
              {deletion.kept > 0 && (
                <li>
                  {deletion.kept === 1
                    ? "1 past appointment stays in the clinic's records, without your name."
                    : `${deletion.kept} past appointments stay in the clinics' records, without your name.`}
                </li>
              )}
              <li>You are welcome back any time.</li>
            </ul>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------------------- Hero */}
      <section className="relative overflow-hidden bg-brand text-on-brand">
        <div className="mesh mesh-hero" aria-hidden />
        <div className="grid-lines" aria-hidden />

        <div className="relative mx-auto grid w-full max-w-6xl gap-12 px-5 py-14 sm:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:py-20">
          <div>
            <p
              className="enter font-mono text-xs uppercase tracking-[0.22em] text-teal"
              style={{ "--enter-delay": "0ms" } as CSSProperties}
            >
              Health &amp; wellness · Lebanon
            </p>

            <h1
              className="enter mt-5 max-w-[13ch] text-balance text-4xl font-extrabold leading-[1.08] sm:text-5xl lg:text-6xl"
              style={{ "--enter-delay": "80ms" } as CSSProperties}
            >
              Find and book <span className="hl">local care</span>
            </h1>

            <p
              className="enter mt-5 max-w-lg text-lg leading-relaxed text-on-brand/70"
              style={{ "--enter-delay": "160ms" } as CSSProperties}
            >
              Search health &amp; wellness businesses across Lebanon, see real
              open times, and request an appointment online.
            </p>

            {/* A real query, straight into discovery. It navigates rather than
                submitting to the server, which is the whole difference between
                the two hosts and changes nothing a visitor can see. */}
            <form
              className="enter mt-8 flex w-full max-w-lg flex-col gap-2 sm:flex-row"
              style={{ "--enter-delay": "240ms" } as CSSProperties}
              onSubmit={(event) => {
                event.preventDefault();
                const trimmed = query.trim();
                void navigate(
                  trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : "/search",
                );
              }}
            >
              <div className="relative flex-1">
                <Search
                  className="pointer-events-none absolute start-4 top-1/2 size-5 -translate-y-1/2 text-muted"
                  aria-hidden
                />
                <input
                  type="search"
                  name="q"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Dentist, physio, clinic name…"
                  aria-label="Search by name or category"
                  className="h-13 w-full rounded-xl border border-transparent bg-surface py-3 ps-12 pe-4 text-ink outline-none transition placeholder:text-muted focus-visible:border-teal"
                />
              </div>
              <button
                type="submit"
                className={buttonClass("onBrand", "h-13 shrink-0 px-7 text-base")}
              >
                Find care
              </button>
            </form>

            {categories.length > 0 && (
              <div
                className="enter mt-7 flex flex-wrap gap-2"
                style={{ "--enter-delay": "320ms" } as CSSProperties}
              >
                {/* The drawn glyph, not just the word. On the front door the
                    chips are the first thing that says what kind of directory
                    this is, and a row of plain words could be any marketplace. */}
                {categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/search?category=${category.id}`}
                    className="inline-flex items-center gap-2 rounded-lg border border-on-brand/20 bg-on-brand/5 py-1.5 pe-4 ps-3 text-sm text-on-brand/80 transition duration-150 hover:border-teal/60 hover:bg-teal/10 hover:text-on-brand"
                  >
                    <CategoryIcon
                      name={category.name}
                      className="size-[18px] shrink-0"
                    />
                    {category.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div
            className="enter relative hidden lg:block"
            style={{ "--enter-delay": "400ms" } as CSSProperties}
          >
            <div
              className="halo start-1/2 top-1/2 size-96 -translate-x-1/2 -translate-y-1/2 opacity-40"
              aria-hidden
            />
            <AppointmentPreview />
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- How it works */}
      <section className="relative overflow-hidden">
        <div className="mesh opacity-70" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-5 py-20">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-accent">
              How it works
            </p>
            <h2 className="mt-3 max-w-xl text-3xl font-extrabold text-ink sm:text-4xl">
              Three steps, no phone calls
            </h2>
          </Reveal>

          <ol className="mt-12 grid gap-6 sm:grid-cols-3">
            {STEPS.map((step, index) => (
              <Reveal as="li" key={step.title} delay={index * 90}>
                <div className="lift h-full rounded-2xl border border-line bg-surface p-7 shadow-[var(--shadow-card)]">
                  <div className="flex items-center gap-3">
                    <span className="flex size-11 items-center justify-center rounded-xl bg-accent/12 text-accent">
                      <step.icon className="size-5.5" aria-hidden />
                    </span>
                    <span className="font-mono text-sm text-muted">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-ink">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed">{step.body}</p>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ------------------------------------------------------ For businesses */}
      <section className="mx-auto w-full max-w-6xl px-5 pb-20">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-brand px-8 py-12 text-on-brand sm:px-12">
            <div className="mesh mesh-hero" aria-hidden />
            <div className="relative flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
              <div>
                <h2 className="text-2xl font-extrabold sm:text-3xl">
                  Run a health business?
                </h2>
                <p className="mt-2 max-w-md text-on-brand/70">
                  List your services, set your schedule, and manage appointment
                  requests in one place.
                </p>
              </div>
              <Link
                to="/join"
                className={buttonClass("onBrand", "shrink-0 px-7 py-3 text-base")}
              >
                List your business
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </PublicPage>
  );
}

/**
 * A static illustration of a confirmed appointment: the shape of the product,
 * not data. `aria-hidden` so screen readers skip the decorative mock, and
 * captioned in the UI so nobody reads it as a real listing.
 */
function AppointmentPreview() {
  return (
    <div className="relative" aria-hidden>
      <div className="rounded-2xl border border-white/10 bg-surface p-5 shadow-[var(--shadow-lift)]">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted">
            Example
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-accent/12 px-2.5 py-1 text-xs font-semibold text-accent">
            <CircleCheck className="size-3.5" />
            Confirmed
          </span>
        </div>

        <div className="mt-4 flex items-start gap-3">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-accent/12 font-display text-lg font-extrabold text-accent">
            C
          </div>
          <div className="min-w-0">
            <p className="font-display text-base font-bold text-ink">
              Cedars Dental Clinic
            </p>
            <p className="mt-0.5 flex items-center gap-3 text-xs text-muted">
              <span className="inline-flex items-center gap-1">
                <MapPin className="size-3.5" /> Beirut
              </span>
              <span className="inline-flex items-center gap-1">
                <Star className="size-3.5 fill-accent text-accent" /> 4.8
              </span>
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-2">
          {[
            { label: "Service", value: "Cleaning · 45 min" },
            { label: "When", value: "Tue 14:30" },
            { label: "From", value: "$40" },
          ].map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between rounded-lg bg-page px-3 py-2.5"
            >
              <span className="text-xs text-muted">{row.label}</span>
              <span className="font-mono text-sm font-semibold text-ink">
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Trailing card, the other side of the flow: a request still pending */}
      <div className="mt-4 ms-auto w-64 rounded-2xl border border-white/10 bg-surface/95 p-4 shadow-[var(--shadow-card)]">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-line px-2.5 py-1 text-xs font-semibold text-body">
          Awaiting confirmation
        </span>
        <p className="mt-2 font-display text-sm font-bold text-ink">
          Physio · Thu 10:00
        </p>
      </div>
    </div>
  );
}
